'use client';

/**
 * Project override of the core category slider.
 *
 * The core version hides categories behind horizontal scroll arrows. Here the bar shows a
 * "Kategórie" trigger plus as many top-level categories as fit on one line (with a "+N" chip for
 * the rest), and both open a mega menu that lists every category at once with its subcategories.
 *
 * The export name and props stay compatible with the core component, so every core usage
 * (header navigation, category button, …) keeps working.
 */

import { CacheKey } from '@eliadmin/core/src/api/constants';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { fetchCategories } from '@eliadmin/core/src/api/products/product-api';
import { buildWhereParams, storeApiResources } from '@eliadmin/core/src/api/store-list-request';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import type {
  AnchorHTMLAttributes,
  FocusEvent as ReactFocusEvent,
  HTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuArrowRight, LuChevronDown, LuChevronRight, LuLayoutGrid } from 'react-icons/lu';

/** Hover intent before the menu opens from the bar, so passing the cursor over it does nothing. */
const HOVER_OPEN_DELAY_MS = 200;
/** Grace period for the cursor to leave the menu briefly without closing it. */
const HOVER_CLOSE_DELAY_MS = 250;
/** Row switch delay, so moving diagonally towards the subcategories keeps the current row. */
const ROW_ACTIVATE_DELAY_MS = 90;
const GRANDCHILDREN_LIMIT = 5;

type CategorySliderClassName = {
  main?: string;
  controlButton?: string;
  list?: string;
  listItem?: string;
  listItemImage?: string;
  listItemChevron?: string;
  link?: string;

  hoverCard?: string;
  hoverCardHeader?: string;
  hoverCardHeaderTitle?: string;
  hoverCardContent?: string;
  hoverCardList?: string;
  hoverCardListItem?: string;
  hoverCardLink?: string;
};

export interface CategorySliderProps {
  className?: CategorySliderClassName;
  style?: HTMLAttributes<HTMLElement>['style'];
  categories?: CategoriesEntity[];
  expandable?: boolean;
  showImage?: boolean;
}

type OpenMode = 'click' | 'hover';

const hasChildCategories = (category: CategoriesEntity) =>
  Boolean(category.has_childs ?? category.childs?.length);

const toCategoryHref = (slug?: string | null) => (slug?.trim() ? getSafeHref(slug) : undefined);

const categoryKey = (category: CategoriesEntity, index: number) =>
  `${category.id ?? category.slug ?? 'category'}-${index}`;

/** Same query key and payload as the core slider, so both share the react-query cache. */
const categoryChildrenQuery = (locale: string, categoryId: CategoriesEntity['id'] | undefined) =>
  queryOptions({
    queryKey: [CacheKey.CATEGORIES, locale, categoryId],
    staleTime: 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetchCategories({
        locale,
        query: {
          childs: true,
          ...buildWhereParams(
            { parent: { equals: categoryId! } },
            storeApiResources.storeCategories,
          ),
          image: true,
          page: 1,
          results: 100,
        },
      }).then(res =>
        res.data.map(category => ({
          ...category,
          has_childs: category.has_childs ?? Boolean(category.childs?.length),
          childs: category.childs ?? null,
        })),
      ),
  });

type CategoryLinkProps = {
  href?: string;
  prefetchOnHover?: boolean;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

const CategoryLink = forwardRef<HTMLAnchorElement, CategoryLinkProps>(
  ({ href, prefetchOnHover, ...props }, ref) => {
    if (!href || href === '#') return <span {...(props as HTMLAttributes<HTMLSpanElement>)} />;
    return isRelativeHref(href) ? (
      <Link ref={ref} href={href} prefetchOnHover={prefetchOnHover} {...props} />
    ) : (
      <a ref={ref} href={href} {...props} />
    );
  },
);
CategoryLink.displayName = 'CategoryLink';

type CategoryImage = NonNullable<CategoriesEntity['icon']>;

const hasImage = (image?: CategoryImage | null): image is CategoryImage => Boolean(image?.url);

const mostHave = <T,>(list: T[], predicate: (item: T) => boolean) =>
  list.length > 0 && list.filter(predicate).length * 2 >= list.length;

/**
 * Only some categories have an icon/image. With `placeholder` the slot is kept for the others
 * too, so names in a list or grid stay aligned.
 */
function CategoryThumb({
  image,
  name,
  size,
  placeholder = false,
  className,
}: {
  image?: CategoryImage | null;
  name: string;
  size: number;
  placeholder?: boolean;
  className?: string;
}) {
  const boxClassName = cn(
    'flex shrink-0 items-center justify-center overflow-hidden rounded-md',
    className,
  );

  if (!hasImage(image)) {
    if (!placeholder) return null;
    return (
      <span aria-hidden className={boxClassName} style={{ width: size, height: size }}>
        {size >= 40 && <LuLayoutGrid className="h-1/3 w-1/3 text-muted-foreground/60" />}
      </span>
    );
  }

  return (
    <span className={cn(boxClassName, 'bg-white')} style={{ width: size, height: size }}>
      <Image
        alt={resolveImageAlt(image, name, undefined)}
        src={image.url}
        width={size}
        height={size}
        sizes={buildImageSizes([{ size: `${size}px` }])}
        className="h-full w-full object-contain"
      />
    </span>
  );
}

export const CategorySlider = ({
  className,
  style,
  categories,
  expandable = true,
  showImage = true,
}: CategorySliderProps) => {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const panelId = useId();

  const rootRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [firstHiddenIndex, setFirstHiddenIndex] = useState<number | null>(null);
  const [overlayTop, setOverlayTop] = useState(0);

  const openModeRef = useRef<OpenMode>('click');
  const pointerTypeRef = useRef<string>('mouse');
  const pendingFocusRowRef = useRef<number | null>(null);
  const timersRef = useRef<{
    open?: ReturnType<typeof setTimeout>;
    close?: ReturnType<typeof setTimeout>;
    row?: ReturnType<typeof setTimeout>;
  }>({});

  // Rows without a name or icon would render as empty boxes.
  const items = useMemo(
    () => (categories ?? []).filter(category => category.name?.trim() || category.icon),
    [categories],
  );

  const safeActiveIndex = Math.min(activeIndex, Math.max(items.length - 1, 0));
  const activeCategory = items[safeActiveIndex];
  const activeHasChildren = activeCategory ? hasChildCategories(activeCategory) : false;
  const embeddedChildren = activeCategory?.childs?.length ? activeCategory.childs : null;

  const childrenQuery = useQuery({
    ...categoryChildrenQuery(locale, activeCategory?.id),
    enabled: Boolean(
      open && expandable && activeCategory?.id && activeHasChildren && !embeddedChildren,
    ),
  });
  const activeChildren = embeddedChildren ?? childrenQuery.data ?? [];
  const isLoadingChildren = !embeddedChildren && activeHasChildren && childrenQuery.isPending;

  const clearTimer = useCallback((name: 'open' | 'close' | 'row') => {
    const timer = timersRef.current[name];
    if (timer) clearTimeout(timer);
    timersRef.current[name] = undefined;
  }, []);

  const clearTimers = useCallback(() => {
    clearTimer('open');
    clearTimer('close');
    clearTimer('row');
  }, [clearTimer]);

  const prefetchChildren = useCallback(
    (category?: CategoriesEntity) => {
      if (!expandable || !category?.id || !hasChildCategories(category) || category.childs?.length)
        return;
      void queryClient.prefetchQuery(categoryChildrenQuery(locale, category.id));
    },
    [expandable, locale, queryClient],
  );

  const openMenu = useCallback(
    (mode: OpenMode, index?: number) => {
      clearTimers();
      openModeRef.current = mode;
      if (index !== undefined) setActiveIndex(index);
      setOpen(true);
    },
    [clearTimers],
  );

  const closeMenu = useCallback(
    (returnFocus = false) => {
      clearTimers();
      pendingFocusRowRef.current = null;
      setOpen(false);
      if (returnFocus) triggerRef.current?.focus();
    },
    [clearTimers],
  );

  const getRows = () =>
    Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[data-ea-category-row]') ?? []);

  const focusRow = (index: number) => {
    const rows = getRows();
    if (!rows.length) return;
    rows[(index + rows.length) % rows.length]?.focus();
  };

  // --- lifecycle -------------------------------------------------------------------------------

  useEffect(() => clearTimers, [clearTimers]);

  // Navigating anywhere (menu link, browser back, search, …) closes the menu.
  useEffect(() => {
    closeMenu();
  }, [pathname, closeMenu]);

  // Focus a row requested before the panel was visible (keyboard opening).
  useEffect(() => {
    if (!open || pendingFocusRowRef.current === null) return;
    focusRow(pendingFocusRowRef.current);
    pendingFocusRowRef.current = null;
  });

  // Outside click and Escape.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      closeMenu();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const focusInside = rootRef.current?.contains(document.activeElement) ?? false;
      closeMenu(focusInside);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, closeMenu]);

  // Keep the dimming overlay glued to the bottom of the bar while the page scrolls.
  useEffect(() => {
    if (!open) return;

    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const bottom = rootRef.current?.getBoundingClientRect().bottom ?? 0;
        setOverlayTop(Math.max(0, Math.round(bottom)));
      });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // Find which bar items wrapped to the (clipped) second line.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const measure = () => {
      const children = Array.from(list.children) as HTMLElement[];
      const firstTop = children[0]?.offsetTop ?? 0;
      const hiddenIndex = children.findIndex(child => child.offsetTop > firstTop + 2);
      setFirstHiddenIndex(hiddenIndex === -1 ? null : hiddenIndex);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    void document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [items]);

  // --- handlers --------------------------------------------------------------------------------

  const isMouse = (event: ReactPointerEvent) => event.pointerType === 'mouse';
  const isTouchPointer = () =>
    pointerTypeRef.current === 'touch' || pointerTypeRef.current === 'pen';

  const scheduleHoverOpen = (index: number) => {
    clearTimer('close');
    if (!expandable) return;

    if (open) {
      clearTimer('open');
      setActiveIndex(index);
      return;
    }

    clearTimer('open');
    prefetchChildren(items[index]);
    timersRef.current.open = setTimeout(() => openMenu('hover', index), HOVER_OPEN_DELAY_MS);
  };

  const handleRootPointerEnter = (event: ReactPointerEvent) => {
    if (isMouse(event)) clearTimer('close');
  };

  const handleRootPointerLeave = (event: ReactPointerEvent) => {
    if (!isMouse(event)) return;
    clearTimer('open');
    if (!open || openModeRef.current !== 'hover') return;
    clearTimer('close');
    timersRef.current.close = setTimeout(() => closeMenu(), HOVER_CLOSE_DELAY_MS);
  };

  const handleRootBlur = (event: ReactFocusEvent<HTMLElement>) => {
    if (!open) return;
    const next = event.relatedTarget as Node | null;
    if (next && !rootRef.current?.contains(next)) closeMenu();
  };

  const handleTriggerClick = () => {
    if (open && openModeRef.current === 'click') {
      closeMenu();
      return;
    }
    openMenu('click');
  };

  const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowDown') return;
    event.preventDefault();
    pendingFocusRowRef.current = safeActiveIndex;
    openMenu('click');
  };

  const handleMoreClick = () => {
    if (open && openModeRef.current === 'click') {
      closeMenu();
      return;
    }
    openMenu('click', firstHiddenIndex ?? undefined);
  };

  const handleRowPointerEnter = (event: ReactPointerEvent, index: number) => {
    if (!isMouse(event)) return;
    clearTimer('row');
    prefetchChildren(items[index]);
    if (index === safeActiveIndex) return;
    timersRef.current.row = setTimeout(() => setActiveIndex(index), ROW_ACTIVATE_DELAY_MS);
  };

  const handleRowClick = (event: ReactMouseEvent, index: number) => {
    const category = items[index];

    // On touch there is no hover: the first tap reveals subcategories, the second one navigates.
    if (
      isTouchPointer() &&
      expandable &&
      hasChildCategories(category) &&
      index !== safeActiveIndex
    ) {
      event.preventDefault();
      setActiveIndex(index);
      return;
    }
    closeMenu();
  };

  const handleRowKeyDown = (event: ReactKeyboardEvent<HTMLElement>, index: number) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusRow(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusRow(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusRow(0);
        break;
      case 'End':
        event.preventDefault();
        focusRow(-1);
        break;
      case 'ArrowRight': {
        const firstLink = contentRef.current?.querySelector<HTMLElement>('a[href]');
        if (!firstLink) return;
        event.preventDefault();
        firstLink.focus();
        break;
      }
    }
  };

  const handleContentKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowLeft') return;
    event.preventDefault();
    focusRow(safeActiveIndex);
  };

  if (!items.length) return null;

  const hiddenCount = firstHiddenIndex === null ? 0 : items.length - firstHiddenIndex;
  const activeHref = toCategoryHref(activeCategory?.slug);
  // Thumbnails only when most entries have one; a column of empty slots looks worse than none.
  const railHasIcons = showImage && mostHave(items, category => hasImage(category.icon));
  const childrenHaveThumbs =
    showImage && mostHave(activeChildren, child => hasImage(child.image ?? child.icon));

  return (
    <>
      <nav
        ref={rootRef}
        aria-label={t('text176')}
        className={cn(
          'relative flex w-full items-center gap-2 py-2',
          open && 'z-50',
          'ea-category-slider__main',
          className?.main,
        )}
        style={style}
        onPointerEnter={handleRootPointerEnter}
        onPointerLeave={handleRootPointerLeave}
        onPointerDown={event => {
          pointerTypeRef.current = event.pointerType;
        }}
        onKeyDownCapture={() => {
          pointerTypeRef.current = 'keyboard';
        }}
        onBlur={handleRootBlur}
      >
        <button
          ref={triggerRef}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          className={cn(
            'inline-flex h-10 shrink-0 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors',
            'hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'ea-category-slider__trigger',
            className?.controlButton,
          )}
          onClick={handleTriggerClick}
          onKeyDown={handleTriggerKeyDown}
          onPointerEnter={event => {
            if (isMouse(event)) scheduleHoverOpen(safeActiveIndex);
          }}
          onPointerLeave={() => clearTimer('open')}
        >
          <LuLayoutGrid className="h-4 w-4" aria-hidden />
          {t('text176')}
          <LuChevronDown
            aria-hidden
            className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')}
          />
        </button>

        {/* The panel follows the trigger in the DOM so Tab moves straight into the open menu. */}
        <div
          ref={panelRef}
          id={panelId}
          hidden={!open}
          className={cn(
            open ? 'flex' : 'hidden',
            // Capped height; the category list and the subcategories scroll independently inside.
            'absolute inset-x-0 top-full max-h-[75dvh] overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-2xl',
            'duration-150 animate-in fade-in-0 slide-in-from-top-1',
            'ea-category-slider__hover-card',
            className?.hoverCard,
          )}
        >
          {/* all top-level categories */}
          <div
            className={cn(
              'min-h-0 overflow-y-auto overscroll-contain py-3',
              expandable ? 'w-64 shrink-0 border-r bg-secondary/60 lg:w-72' : 'w-full',
            )}
          >
            <span className="block px-5 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('text2319')}
            </span>
            <ul
              className={cn(
                !expandable && 'grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
                'ea-category-slider__hover-card-list',
                className?.hoverCardList,
              )}
            >
              {items.map((category, index) => {
                const isActive = expandable && index === safeActiveIndex;
                const withChildren = expandable && hasChildCategories(category);

                return (
                  <li key={categoryKey(category, index)}>
                    <CategoryLink
                      data-ea-category-row=""
                      data-active={isActive}
                      href={toCategoryHref(category.slug)}
                      prefetchOnHover
                      className={cn(
                        'group relative flex min-h-9 items-center gap-3 px-5 py-1.5 text-sm font-medium leading-snug text-foreground/85 transition-colors',
                        'before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:rounded-r-full before:bg-primary before:opacity-0 before:transition-opacity',
                        'hover:bg-background hover:text-foreground focus-visible:bg-background focus-visible:text-foreground focus-visible:outline-none',
                        'data-[active=true]:bg-background data-[active=true]:font-semibold data-[active=true]:text-foreground data-[active=true]:before:opacity-100',
                        'ea-category-slider__hover-card-link',
                        className?.hoverCardLink,
                      )}
                      onPointerEnter={event => handleRowPointerEnter(event, index)}
                      onPointerLeave={() => clearTimer('row')}
                      onFocus={() => {
                        // A tap focuses before it clicks; activating here would turn the first tap
                        // into navigation instead of revealing the subcategories.
                        if (!expandable || isTouchPointer()) return;
                        clearTimer('row');
                        setActiveIndex(index);
                      }}
                      onClick={event => handleRowClick(event, index)}
                      onKeyDown={event => handleRowKeyDown(event, index)}
                    >
                      {railHasIcons && (
                        <CategoryThumb
                          image={category.icon}
                          name={category.name}
                          size={22}
                          placeholder
                        />
                      )}
                      <span className="flex-1">{category.name}</span>
                      {withChildren && (
                        <LuChevronRight
                          aria-hidden
                          className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground group-data-[active=true]:text-primary"
                        />
                      )}
                    </CategoryLink>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* subcategories of the active category */}
          {expandable && activeCategory && (
            <div
              ref={contentRef}
              className={cn(
                'min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain p-6 lg:p-8',
                'ea-category-slider__hover-card-content',
                className?.hoverCardContent,
              )}
              onKeyDown={handleContentKeyDown}
            >
              <div
                className={cn(
                  'mb-6 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b pb-4',
                  'ea-category-slider__hover-card-header',
                  className?.hoverCardHeader,
                )}
              >
                <span
                  className={cn(
                    'text-xl font-bold leading-tight',
                    'ea-category-slider__hover-card-title',
                    className?.hoverCardHeaderTitle,
                  )}
                >
                  {activeCategory.name}
                </span>
                {activeHref && (
                  <CategoryLink
                    href={activeHref}
                    className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline focus-visible:underline focus-visible:outline-none"
                    onClick={() => closeMenu()}
                  >
                    {t('text3161')}
                    <LuArrowRight
                      aria-hidden
                      className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                    />
                  </CategoryLink>
                )}
              </div>

              {isLoadingChildren ? (
                <div className="grid grid-cols-1 gap-x-8 gap-y-7 md:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="animate-pulse space-y-3">
                      <div className="h-4 w-2/3 rounded bg-muted" />
                      <div className="h-3 w-1/2 rounded bg-muted/70" />
                      <div className="h-3 w-3/5 rounded bg-muted/70" />
                    </div>
                  ))}
                </div>
              ) : activeChildren.length > 0 ? (
                <ul className="grid grid-cols-1 gap-x-8 gap-y-7 md:grid-cols-2 xl:grid-cols-3">
                  {activeChildren.map((child, index) => {
                    const grandchildren = child.childs ?? [];
                    const childHref = toCategoryHref(child.slug);

                    return (
                      <li
                        key={categoryKey(child, index)}
                        className={cn('min-w-0', 'ea-category__hover-card-list-item')}
                      >
                        <CategoryLink
                          href={childHref}
                          className="group flex items-center gap-3 text-[15px] font-semibold leading-snug transition-colors hover:text-primary focus-visible:text-primary focus-visible:outline-none"
                          onClick={() => closeMenu()}
                        >
                          {childrenHaveThumbs && (
                            <CategoryThumb
                              image={child.image ?? child.icon}
                              name={child.name}
                              size={48}
                              placeholder
                              className="border bg-secondary/60 p-1"
                            />
                          )}
                          <span>{child.name}</span>
                        </CategoryLink>

                        {grandchildren.length > 0 && (
                          <ul
                            className={cn('mt-2 space-y-1.5', childrenHaveThumbs && 'pl-[3.75rem]')}
                          >
                            {grandchildren.slice(0, GRANDCHILDREN_LIMIT).map((grandchild, i) => (
                              <li key={categoryKey(grandchild, i)}>
                                <CategoryLink
                                  href={toCategoryHref(grandchild.slug)}
                                  className="text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline focus-visible:text-foreground focus-visible:underline focus-visible:outline-none"
                                  onClick={() => closeMenu()}
                                >
                                  {grandchild.name}
                                </CategoryLink>
                              </li>
                            ))}
                            {grandchildren.length > GRANDCHILDREN_LIMIT && childHref && (
                              <li>
                                <CategoryLink
                                  href={childHref}
                                  className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline focus-visible:underline focus-visible:outline-none"
                                  onClick={() => closeMenu()}
                                >
                                  {t('text338')} ({grandchildren.length - GRANDCHILDREN_LIMIT})
                                  <LuChevronRight aria-hidden className="h-3.5 w-3.5" />
                                </CategoryLink>
                              </li>
                            )}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                // No subcategories (or they failed to load): offer the category itself.
                <div className="flex flex-col items-start gap-4 py-2">
                  {showImage && (
                    <CategoryThumb
                      image={activeCategory.image ?? activeCategory.icon}
                      name={activeCategory.name}
                      size={96}
                      className="border p-2"
                    />
                  )}
                  {activeHref && (
                    <CategoryLink
                      href={activeHref}
                      className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      onClick={() => closeMenu()}
                    >
                      {t('text670')}
                      <LuArrowRight aria-hidden className="h-4 w-4" />
                    </CategoryLink>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* One line of shortcuts; whatever wraps is clipped and counted in the "+N" chip. */}
        <ul
          ref={listRef}
          className={cn(
            'relative flex h-10 min-w-0 flex-1 flex-wrap items-center overflow-hidden',
            'ea-category-slider__list',
            className?.list,
          )}
        >
          {items.map((category, index) => {
            const isHidden = firstHiddenIndex !== null && index >= firstHiddenIndex;
            const withChildren = expandable && hasChildCategories(category);

            return (
              <li
                key={categoryKey(category, index)}
                aria-hidden={isHidden || undefined}
                className={cn(
                  'flex h-10 items-center',
                  isHidden && 'invisible',
                  'ea-category-slider__list-item',
                  className?.listItem,
                )}
                onPointerEnter={event => {
                  if (isMouse(event) && withChildren) scheduleHoverOpen(index);
                  else if (isMouse(event) && open) setActiveIndex(index);
                }}
                onPointerLeave={() => clearTimer('open')}
              >
                <CategoryLink
                  href={toCategoryHref(category.slug)}
                  prefetchOnHover
                  tabIndex={isHidden ? -1 : undefined}
                  className={cn(
                    'flex h-9 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-semibold text-foreground/80 transition-colors',
                    'hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    open && index === safeActiveIndex && 'bg-secondary text-foreground',
                    'ea-category-slider__list-item-link',
                    className?.link,
                  )}
                  onClick={() => closeMenu()}
                >
                  {category.name}
                </CategoryLink>
              </li>
            );
          })}
        </ul>

        {hiddenCount > 0 && (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={`${t('text2319')} (+${hiddenCount})`}
            title={t('text2319')}
            className={cn(
              'inline-flex h-9 min-w-[3.5rem] shrink-0 items-center justify-center gap-1 rounded-full border px-3 text-sm font-semibold text-foreground/80 transition-colors',
              'hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'ea-category-slider__more',
            )}
            onClick={handleMoreClick}
          >
            +{hiddenCount}
            <LuChevronDown aria-hidden className="h-3.5 w-3.5" />
          </button>
        )}
      </nav>

      {/* Rendered outside <nav>: React dispatches pointerleave along the React tree, so a portal
        inside the nav would count as "still inside" and hover mode would never close. */}
      {open &&
        createPortal(
          <div
            aria-hidden
            className="fixed inset-x-0 bottom-0 z-40 bg-black/30 duration-150 animate-in fade-in-0"
            style={{ top: overlayTop }}
          />,
          document.body,
        )}
    </>
  );
};
